import {
    AppTable,
    EnrollmentLinkDisplayModal,
    EnrollmentLinkDisplayModalMode,
} from 'components/organisms';
import { commonTexts, enrollmentTexts } from 'consts/text';
import { ApplicationStatus, EnrollmentId, EnrollmentItem } from 'types/enrollment/enrollment';
import { ChildId } from 'types';
import { usePageFilters } from 'hooks/usePageFilters/usePageFilters';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import {
    ChildEnrollmentApproveModal,
    ChildEnrollmentRejectModal,
    EnrollmentFinishModal,
} from 'components/templates';
import { appPaths } from 'consts/paths/paths';
import { noop } from 'lodash';
import { generatePath } from 'react-router-dom';
import { AppButton, AppFlex, AppTooltipQuestionMarkIcon, Option } from 'components/atoms';
import {
    IconDoorEnter,
    IconEdit,
    IconHomeMove,
    IconMailbox,
    IconMailboxOff,
    IconUserX,
} from '@tabler/icons-react';
import { parseFilterParamsIntoGetEnrollmentFiltersValues } from 'utils/parsers/enrollment/parseFilterParamsIntoGetEnrollmentFiltersValues';
import { useTheme } from 'styled-components';
import { parsePaginationDataToTablePaginationData } from 'utils/parsers/table/parsePaginationDataToTablePaginationData';
import { useSearchParamsBasedPagination } from 'hooks/useSearchParamsBasedPagination/useSearchParamsBasedPagination';
import { useState } from 'react';
import { EnrollmentMoveChildrenModal } from 'components/organisms/EnrollmentMoveChildrenModal/EnrollmentMoveChildrenModal';
import { EnrollmentPageFilters } from './components/EnrollmentPageFilters/EnrollmentPageFilters';
import { columns, filters } from './EnrollmentPage.utils';

enum EnrollmentPageModal {
    CHILD_ENROLLMENT_APPROVE = 'CHILD_ENROLLMENT_APPROVE',
    CHILD_ENROLLMENT_REJECT = 'CHILD_ENROLLMENT_REJECT',
    ENROLLMENT_LINK_DISPLAY = 'ENROLLMENT_LINK_DISPLAY',
    MOVE_CHILDREN = 'MOVE_CHILDREN',
    FINISH_ENROLLMENT = 'FINISH_ENROLLMENT',
}

export const EnrollmentPage = () => {
    // TODO: to add function to update
    const [newEnrollmentLink] = useState<string | null>(null);
    const [openModal, setOpenModal] = useState<EnrollmentPageModal | null>(null);

    const { currentPage, currentPageSize, handlePaginationValuesChange } =
        useSearchParamsBasedPagination();

    const { statefulNavigate } = useNavigateWithState();

    const {
        filtersParams,
        searchFilterDebounceValue,
        searchFilterLocalValue,
        handleChangeFilter,
        handleChangeFilters,
        handleClearFilters,
        handleSearchInput,
    } = usePageFilters({ filters, currentPage });

    const theme = useTheme();

    const filtersValues = parseFilterParamsIntoGetEnrollmentFiltersValues(
        filtersParams,
        searchFilterDebounceValue,
    );

    const handleEditEnrollmentItemClick = (enrollmentItem: EnrollmentItem) => {
        const generatedPath = generatePath(appPaths.app.enrollment.edit.pickedEnrollmentItem, {
            pickedEnrollmentItemId: enrollmentItem.id,
        });
        statefulNavigate(generatedPath);
    };

    const openEnrollmentLinkDisplayModal = () =>
        setOpenModal(EnrollmentPageModal.ENROLLMENT_LINK_DISPLAY);

    const openMoveChildrenModal = () => setOpenModal(EnrollmentPageModal.MOVE_CHILDREN);

    const openFinishEnrollmentModal = () => setOpenModal(EnrollmentPageModal.FINISH_ENROLLMENT);

    const closeModal = () => setOpenModal(null);

    const options: Option<EnrollmentItem>[] = [
        {
            label: commonTexts.actionLabels.edit,
            Icon: <IconEdit />,
            onClick: handleEditEnrollmentItemClick,
        },
        {
            label: commonTexts.actionLabels.approveApplication,
            Icon: <IconDoorEnter />,
            checkIfVisible: (enrollmentItem) => enrollmentItem.status === ApplicationStatus.NEW,
        },
        {
            label: commonTexts.actionLabels.rejectApplication,
            Icon: <IconUserX />,
            checkIfVisible: (enrollmentItem) => enrollmentItem.status === ApplicationStatus.NEW,
        },
    ];

    // TODO: to pass existing link or newly generated link
    const enrollmentLink = newEnrollmentLink || '';

    return (
        <>
            <EnrollmentMoveChildrenModal
                open={openModal === EnrollmentPageModal.MOVE_CHILDREN}
                onCancel={closeModal}
            />
            <EnrollmentLinkDisplayModal
                open={openModal === EnrollmentPageModal.ENROLLMENT_LINK_DISPLAY}
                link={enrollmentLink}
                mode={
                    newEnrollmentLink
                        ? EnrollmentLinkDisplayModalMode.SUCCESS
                        : EnrollmentLinkDisplayModalMode.DEFAULT
                }
                onCancel={closeModal}
            />
            <ChildEnrollmentApproveModal
                open={openModal === EnrollmentPageModal.CHILD_ENROLLMENT_APPROVE}
                onCancel={closeModal}
            />
            <ChildEnrollmentRejectModal
                open={openModal === EnrollmentPageModal.CHILD_ENROLLMENT_REJECT}
                onCancel={closeModal}
            />
            <EnrollmentFinishModal
                open={openModal === EnrollmentPageModal.FINISH_ENROLLMENT}
                onCancel={closeModal}
            />
            <AppTable<EnrollmentItem>
                title={enrollmentTexts.enrollmentTable.title}
                emptyInfo={enrollmentTexts.enrollmentTable.noChildren}
                options={options}
                columns={columns}
                // TODO: to pass data from backend
                dataSource={[
                    {
                        id: '1' as EnrollmentId,
                        schoolYear: '2024/2025',
                        status: ApplicationStatus.NEW,
                        child: {
                            id: '1' as ChildId,
                            firstName: 'Alla',
                            lastName: 'Makota',
                            sex: 'MALE',
                            address: null,
                        },
                        parent: {
                            firstName: 'parent',
                            lastName: 'Makota',
                            phoneNumber: '111222333',
                        },
                    },
                ]}
                rowKey={({ id }) => id}
                additionalRightTableHeaderContent={
                    // TODO: add logic to show only one button at the same time
                    <AppFlex gap="middle">
                        <AppButton
                            // TODO: to pass generate enrollment link function
                            onClick={enrollmentLink ? openEnrollmentLinkDisplayModal : noop}
                            type="default"
                            icon={<IconMailbox />}
                            margin={0}
                        >
                            <AppFlex gap="small" align="center">
                                {newEnrollmentLink
                                    ? enrollmentTexts.enrollmentTable.displayLinkButton
                                    : enrollmentTexts.enrollmentTable.startEnrollmentButton}
                                <AppTooltipQuestionMarkIcon
                                    tooltip={
                                        enrollmentTexts.enrollmentTable
                                            .startEnrollmentButtonDescription
                                    }
                                    color={theme.colors.grayscale.gray6}
                                />
                            </AppFlex>
                        </AppButton>
                        <AppButton
                            type="default"
                            icon={<IconMailboxOff />}
                            margin={0}
                            onClick={openFinishEnrollmentModal}
                        >
                            {enrollmentTexts.enrollmentTable.finishEnrollmentButton}
                        </AppButton>
                        <AppButton
                            onClick={openMoveChildrenModal}
                            type="primary"
                            icon={<IconHomeMove />}
                            margin={0}
                        >
                            {enrollmentTexts.enrollmentTable.moveChildrenButton}
                        </AppButton>
                    </AppFlex>
                }
                // TODO add buttons
                // TODO connect logic
                // isError={}
                // isDataInitialLoading={}
                // isDataRefetching={}
                pagination={{
                    ...parsePaginationDataToTablePaginationData({
                        currentPage,
                        currentPageSize,
                    }),
                    total: 0,
                    onChange: handlePaginationValuesChange,
                }}
                tableManageUtilsProps={{
                    searchInputProps: {
                        defaultValue: searchFilterDebounceValue,
                        value: searchFilterLocalValue,
                        onInput: handleSearchInput,
                        width: '25%',
                    },
                    additionalFilters: (
                        <EnrollmentPageFilters
                            status={filtersValues.status}
                            fromYear={filtersValues.fromYear}
                            toYear={filtersValues.toYear}
                            onChangeFilter={handleChangeFilter}
                            onChangeFilters={handleChangeFilters}
                        />
                    ),
                    isDisplayedAsSeparateMenuOnMobile: true,
                    onClearFilters: handleClearFilters,
                }}
            />
        </>
    );
};
